import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import BannerWizard from "../components/BannerWizard";
import { fetchActiveBanners } from "../services/bannerService";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ChannelLiveBadge from "../components/ChannelLiveBadge";
import "./Landing.css";
import api from "../utils/api";

function EyeIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2.2 12s3.4-7 9.8-7 9.8 7 9.8 7-3.4 7-9.8 7-9.8-7-9.8-7Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

function EyeOffIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 3l18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M10.6 5.3A10.7 10.7 0 0 1 12 5c6.4 0 9.8 7 9.8 7a18 18 0 0 1-3.1 4.3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M6.2 6.2C3.6 8.4 2.2 12 2.2 12s3.4 7 9.8 7c1.7 0 3.2-.3 4.5-.9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M10.3 10.3a3 3 0 0 0 3.4 3.4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ===============================================================
   HELPERS
================================================================ */

function uniqKeepOrder(arr) {
  const out = [];
  const seen = new Set();
  for (const v of arr || []) {
    const id = String(v || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function normalize(str) {
  return str
    ?.toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

// Generate consistent color from group name
function getGroupColor(groupName) {
  const colors = [
    "#ef4444", // red
    "#f97316", // orange
    "#f59e0b", // amber
    "#eab308", // yellow
    "#84cc16", // lime
    "#22c55e", // green
    "#10b981", // emerald
    "#14b8a6", // teal
    "#06b6d4", // cyan
    "#0ea5e9", // sky
    "#3b82f6", // blue
    "#6366f1", // indigo
    "#8b5cf6", // violet
    "#a855f7", // purple
    "#d946ef", // fuchsia
    "#ec4899", // pink
  ];
  
  let hash = 0;
  for (let i = 0; i < groupName.length; i++) {
    hash = groupName.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
}

function buildDisplay(allChannels, orderArr, favArr) {
  const map = new Map(allChannels.map((c) => [c.id, c]));
  const favSet = new Set(favArr);

  const favs = favArr.map((id) => map.get(id)).filter(Boolean);

  const ordered = orderArr
    .filter((id) => !favSet.has(id))
    .map((id) => map.get(id))
    .filter(Boolean);

  const known = new Set([...favArr, ...orderArr]);
  const extras = allChannels.filter((c) => !known.has(c.id));

  return [...favs, ...ordered, ...extras];
}

function persistLocal(channelsArr, favArr) {
  const stored = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
  localStorage.setItem(
    "adm_panel_user",
    JSON.stringify({
      ...stored,
      channels: channelsArr,
      favchannels: favArr,
    })
  );
}

async function persistBackend({ email, channelsArr, favArr }) {
  try {
    await api.post("/updateci", {
      email,
      channels: channelsArr,
      favchannels: favArr,
    });
  } catch (err) {
    console.warn("persistBackend failed", err?.message);
  }
}

/* ===============================================================
   SORTABLE CARD (FLIP ENABLED – SAFE VERSION)
================================================================ */

function SortableCard({
  c,
  favSet,
  onToggleFavorite,
  navigate,
  dragEnabled,
  hideValues,
  revealedChannelId,
  onToggleRevealSingle,
  groupName,
  groupColor,
  liveMap,
}) {
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({
      id: c.id,
      disabled: !dragEnabled,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isRevealed = revealedChannelId === c.id;
  const isFlipped = hideValues && !isRevealed;

  return (
    <div
      ref={setNodeRef}
      data-flip-id={c.id}
      style={style}
      {...attributes}
      {...(dragEnabled ? listeners : {})}
      className="channel-card"
      onClick={() => {
        localStorage.setItem(
          "adm_panel_channelInfo_" + c.id,
          JSON.stringify({ i: c.id, n: c.name, u: c.logo })
        );
        navigate(`/channel/${c.id}`);
      }}
    >
      {/* Group Badge */}
      {groupName && (
        <div
          className="group-badge"
          style={{ backgroundColor: groupColor }}
        >
          {groupName}
        </div>
      )}

      {/* ⭐ FAVORITE – HER ZAMAN GÖRÜNÜR */}
      <button
        className={`fav-btn ${favSet.has(c.id) ? "active" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(c.id);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        type="button"
      >
        ★
      </button>

      {/* 👁️ EYE – SADECE HIDE AKTİFKEN */}
      {hideValues && (
        <button
          className="eye-btn"
          onClick={(e) => {
            e.stopPropagation();
            onToggleRevealSingle(c.id);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          type="button"
        >
          {isRevealed ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      )}

      {/* 🔄 FLIP EDİLEN TEK ALAN */}
      <div className={`card-inner ${isFlipped ? "flipped" : ""}`}>
        {/* ---------- FRONT ---------- */}
        <div className="card-face card-front">
          <div className="channel-logo-box">
            <img
              loading="lazy"
              src={c.logo}
              alt={c.name}
              className="channel-logo"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/fallback.png";
              }}
            />
          </div>

          <div className="channel-live-wrapper">
            <ChannelLiveBadge channelId={c.id} liveMap={liveMap} />
          </div>

          <div className="channel-info">
            <h4 className="channel-name">{c.name}</h4>
          </div>
        </div>

        {/* ---------- BACK ---------- */}
        <div className="card-face card-back">
          <div className="channel-logo-box">
            <img
              loading="lazy"
              src={c.logo}
              alt={c.name}
              className="channel-logo"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/fallback.png";
              }}
            />
          </div>

          <div className="channel-live-hidden">
            <span className="masked">***</span>
          </div>

          <div className="channel-info">
            <h4 className="channel-name">{c.name}</h4>
          </div>
        </div>
      </div>
    </div>
  );
}



/* ===============================================================
   COMPONENT
================================================================ */

export default function Landing() {
  const navigate = useNavigate();

  /* --------------------------------
     BOOT USER
  -------------------------------- */
  const boot = useMemo(() => {
    const u = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
    return {
      email: (u.email || "").trim(),
      channels: Array.isArray(u.channels) ? u.channels : [],
      favchannels: Array.isArray(u.favchannels) ? u.favchannels : [],
      channelgroup: u.channelgroup || {},
    };
  }, []);

  /* --------------------------------
     STATE
  -------------------------------- */
  const [selectedProvider, setSelectedProvider] = useState("");
  const [channelsArr, setChannelsArr] = useState(boot.channels);
  const [favArr, setFavArr] = useState(boot.favchannels);
  const [allChannels, setAllChannels] = useState([]);
  const [sortMode, setSortMode] = useState("custom");
  const isLiveSort = sortMode === "live" || sortMode === "liveAsc";
  const [liveMap, setLiveMap] = useState({});
  const [sortMap, setSortMap] = useState({});
  const liveRef = useRef({});
  const hoverRef = useRef(false);
  const pendingSortRef = useRef(false);
  const [channelGroups, setChannelGroups] = useState(boot.channelgroup);
  const [banners, setBanners] = useState([]);
  /* Group Management Modal */
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedChannelsForGroup, setSelectedChannelsForGroup] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [channelSearchQuery, setChannelSearchQuery] = useState("");

  /* 🔒 GLOBAL HIDE + SINGLE REVEAL */
  const [hideValues, setHideValues] = useState(
    () => localStorage.getItem("adm_hide_values") === "1"
  );
  const [revealedChannelId, setRevealedChannelId] = useState(null);
  
  /* Collapsible Groups */
  const [expandedGroups, setExpandedGroups] = useState({});
  
  /* View Mode - list or group */
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("adm_view_mode") || "group"
  );

  const toggleHideValues = useCallback(() => {
    setHideValues((v) => {
      const next = !v;
      localStorage.setItem("adm_hide_values", next ? "1" : "0");
      setRevealedChannelId(null); // reset single reveal
      return next;
    });
  }, []);

  // ✅ FIX: single-channel reveal is TOGGLE
  const toggleRevealSingle = useCallback((id) => {
    setRevealedChannelId((prev) => (prev === id ? null : id));
  }, []);

  /* --------------------------------
     NORMALIZED
  -------------------------------- */
  const orderArr = useMemo(() => uniqKeepOrder(channelsArr), [channelsArr]);

  const favorites = useMemo(() => {
    const set = new Set(orderArr);
    return uniqKeepOrder(favArr).filter((id) => set.has(id)).slice(0, 4);
  }, [favArr, orderArr]);

  const favSet = useMemo(() => new Set(favorites), [favorites]);

  /* --------------------------------
     LOAD CHANNEL METADATA
  -------------------------------- */
  useEffect(() => {
    if (!orderArr.length) return;

    let cancelled = false;

    async function loadChannels() {
      try {
        const res = await api.get(`/ci?i=${orderArr.join("-")}`);

        const data = res.data;

        if (cancelled) return;

        setAllChannels(
          (data || []).map((c) => ({
            id: String(c.i || "").trim(),
            name: c.n || "",
            logo: c.u || "",
            provider: c.broadcastProvider || "",
          }))
        );
      } catch (err) {
        console.warn("loadChannels failed", err?.message);
      }
    }

    loadChannels();

    return () => {
      cancelled = true;
    };
  }, [orderArr]);

  useEffect(() => {
    // fetch directly — avoids race condition with localStorage pendingBanners
    fetchActiveBanners()
      .then((activeBanners) => {
        if (activeBanners?.length > 0) {
          setBanners(activeBanners);
        }
      })
      .catch(() => {});

    // clean up any leftover pendingBanners from old flow
    localStorage.removeItem("pendingBanners");
  }, []);
  /* --------------------------------
     DISPLAY LIST
  -------------------------------- */
  const displayedChannels = useMemo(() => {
    let base = buildDisplay(allChannels, orderArr, favorites);

    // Provider filter
    if (selectedProvider) {
      const p = normalize(selectedProvider);
      base = base.filter(
        (c) => normalize(c.provider) === p
      );
    }

    // Sorting
    if (sortMode === "asc" || sortMode === "desc") {
      const pinned = base.filter((c) => favSet.has(c.id));
      const rest = base.filter((c) => !favSet.has(c.id));

      rest.sort((a, b) =>
        sortMode === "asc"
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name)
      );

      return [...pinned, ...rest];
    }

    if (isLiveSort && Object.keys(sortMap).length) {
      const pinned = base.filter((c) => favSet.has(c.id));
      const rest = base.filter((c) => !favSet.has(c.id));
      const measured = (c) => sortMap[c.id] !== undefined;

      rest.sort((a, b) => {
        if (measured(a) !== measured(b)) return measured(a) ? -1 : 1;
        const diff = (sortMap[a.id] || 0) - (sortMap[b.id] || 0);
        return (sortMode === "liveAsc" ? diff : -diff) || a.name.localeCompare(b.name);
      });

      return [...pinned, ...rest];
    }

    return base;
  }, [allChannels, orderArr, favorites, favSet, sortMode, isLiveSort, selectedProvider, sortMap]);

  /* --------------------------------
     FAVORITE TOGGLE
  -------------------------------- */
  const toggleFavorite = useCallback(
    async (id) => {
      if (sortMode !== "custom") return;
      if (!orderArr.includes(id)) return;

      let nextFavs;
      if (favSet.has(id)) {
        nextFavs = favorites.filter((x) => x !== id);
      } else {
        if (favorites.length >= 4) return;
        nextFavs = [id, ...favorites];
      }

      setFavArr(nextFavs);
      persistLocal(orderArr, nextFavs);

      await persistBackend({
        email: boot.email,
        channelsArr: orderArr,
        favArr: nextFavs,
      });
    },
    [sortMode, orderArr, favSet, favorites, boot.email]
  );

  /* --------------------------------
     DND
  -------------------------------- */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const onDragEnd = useCallback(
    async ({ active, over }) => {
      if (!over || active.id === over.id) return;
      if (sortMode !== "custom") return;

      const dndList = displayedChannels.filter((c) => !favSet.has(c.id));
      const dndIds = dndList.map((c) => c.id);

      const oldIndex = dndIds.indexOf(active.id);
      const newIndex = dndIds.indexOf(over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      const movedIds = arrayMove(dndIds, oldIndex, newIndex);

      const nextOrder = uniqKeepOrder([
        ...favorites,
        ...movedIds,
        ...orderArr.filter(
          (id) => !favorites.includes(id) && !movedIds.includes(id)
        ),
      ]);

      setChannelsArr(nextOrder);
      persistLocal(nextOrder, favorites);

      await persistBackend({
        email: boot.email,
        channelsArr: nextOrder,
        favArr: favorites,
      });
    },
    [sortMode, displayedChannels, favSet, favorites, orderArr, boot.email]
  );

  const dragEnabled = sortMode === "custom";
  const liveMapForCards = liveMap;

  /* Live values for all cards: groups of 25 in the user's channel order, a failed group retried once. */
  useEffect(() => {
    if (!allChannels.length) return undefined;

    let cancelled = false;
    const known = new Set(allChannels.map((c) => c.id));
    const ordered = orderArr.filter((id) => known.has(id));
    const inOrder = new Set(ordered);
    const ids = [...ordered, ...allChannels.map((c) => c.id).filter((id) => id && !inOrder.has(id))];

    const fetchChunk = (chunk) =>
      api.get(`/live?c=${chunk.join("-")}`).then((r) => r.data || {});

    const fetchWithRetry = async (chunk) => {
      try {
        return await fetchChunk(chunk);
      } catch {
        await new Promise((resolve) => setTimeout(resolve, 2500));
        return fetchChunk(chunk);
      }
    };

    const load = async () => {
      const chunks = [];
      for (let i = 0; i < ids.length; i += 25) chunks.push(ids.slice(i, i + 25));

      const results = await Promise.allSettled(chunks.map(fetchWithRetry));
      if (cancelled) return;

      const merged = { ...liveRef.current };
      results.forEach((r) => {
        if (r.status !== "fulfilled") return;
        Object.entries(r.value).forEach(([id, v]) => {
          merged[id] = Number(v) || 0;
        });
      });
      liveRef.current = merged;
      setLiveMap(merged);

      // Cards stay where they are while the pointer is over one.
      if (hoverRef.current) pendingSortRef.current = true;
      else setSortMap(merged);
    };

    load();
    const timer = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [allChannels, orderArr]);

  useEffect(() => {
    if (!isLiveSort) return undefined;

    const onOver = (e) => {
      hoverRef.current = !!e.target.closest?.(".channel-card");
      if (!hoverRef.current && pendingSortRef.current) {
        pendingSortRef.current = false;
        setSortMap(liveRef.current);
      }
    };
    document.addEventListener("mouseover", onOver);
    return () => document.removeEventListener("mouseover", onOver);
  }, [isLiveSort]);

  /* Cards slide to their new place when the order changes. */
  const flipRef = useRef({ order: "", rects: null });
  useLayoutEffect(() => {
    if (!isLiveSort) {
      flipRef.current = { order: "", rects: null };
      return;
    }

    const order = displayedChannels.map((c) => c.id).join(",");
    const rects = new Map();
    const els = document.querySelectorAll("[data-flip-id]");
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      rects.set(el.dataset.flipId, {
        left: r.left + window.scrollX,
        top: r.top + window.scrollY,
      });
    });

    const prev = flipRef.current;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    if (prev.rects && prev.order !== order && !reduced) {
      els.forEach((el) => {
        const before = prev.rects.get(el.dataset.flipId);
        const after = rects.get(el.dataset.flipId);
        if (!before || !after) return;

        const dx = before.left - after.left;
        const dy = before.top - after.top;
        if (!dx && !dy) return;

        el.style.transition = "none";
        el.style.transform = `translate(${dx}px, ${dy}px)`;
        el.getBoundingClientRect();
        el.style.transition = "transform 600ms cubic-bezier(0.22, 1, 0.36, 1)";
        el.style.transform = "";
        setTimeout(() => {
          el.style.transition = "";
        }, 650);
      });
    }

    flipRef.current = { order, rects };
  });

  /* --------------------------------
     GROUP MANAGEMENT
  -------------------------------- */
  const getChannelGroup = useCallback((channelId) => {
    for (const [groupName, channelIds] of Object.entries(channelGroups)) {
      if (channelIds.includes(channelId)) {
        return { name: groupName, color: getGroupColor(groupName) };
      }
    }
    return null;
  }, [channelGroups]);

  const openGroupModal = useCallback((groupName = null) => {
    if (groupName) {
      setEditingGroup(groupName);
      setNewGroupName(groupName);
      setSelectedChannelsForGroup(channelGroups[groupName] || []);
    } else {
      setEditingGroup(null);
      setNewGroupName("");
      setSelectedChannelsForGroup([]);
    }
    setShowGroupModal(true);
  }, [channelGroups]);

  const closeGroupModal = () => {
    setShowGroupModal(false);
    setEditingGroup(null);
    setNewGroupName("");
    setSelectedChannelsForGroup([]);
    setChannelSearchQuery("");
  };

  const toggleChannelInGroup = (channelId) => {
    setSelectedChannelsForGroup((prev) =>
      prev.includes(channelId)
        ? prev.filter((id) => id !== channelId)
        : [...prev, channelId]
    );
  };

  const saveGroup = async () => {
    const trimmedName = newGroupName.trim();

    if (!trimmedName) {
      alert("Please enter a group name");
      return;
    }

    try {
      await api.post("/save-channel-group", {
        groupname: trimmedName,
        channels: selectedChannelsForGroup,
      });

      const updatedGroups = {
        ...channelGroups,
        [trimmedName]: selectedChannelsForGroup,
      };

      setChannelGroups(updatedGroups);

      // ✅ localStorage sync
      const storedUser = JSON.parse(
        localStorage.getItem("adm_panel_user") || "{}"
      );

      localStorage.setItem(
        "adm_panel_user",
        JSON.stringify({
          ...storedUser,
          channelgroup: updatedGroups,
        })
      );

      closeGroupModal();

    } catch (err) {
      console.error(err);

      if (err.response) {
        alert("Failed to save group");
      } else {
        alert("Server error");
      }
    }
  };

  const deleteGroup = async (groupName) => {
    if (!window.confirm(`Delete group "${groupName}"?`)) return;

    try {
      await api.post("/save-channel-group", {
        groupname: groupName,
        channels: [],
      });

      const { [groupName]: removed, ...restGroups } = channelGroups;

      setChannelGroups(restGroups);

      // ✅ localStorage sync
      const storedUser = JSON.parse(
        localStorage.getItem("adm_panel_user") || "{}"
      );

      localStorage.setItem(
        "adm_panel_user",
        JSON.stringify({
          ...storedUser,
          channelgroup: restGroups,
        })
      );

    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="landing-page">
      <div className="landing-controls">
        <select
          value={selectedProvider}
          onChange={(e) => setSelectedProvider(e.target.value)}
        >
          <option value="">Select Platform</option>
          <option value="Demirören">Demirören</option>
          <option value="Türksat">Türksat</option>
          <option value="Youtube">Youtube</option>
          <option value="Güç Telekom">Güç Telekom</option>
          <option value="Nilesat">Nilesat</option>
          <option value="Arabsat">Arabsat</option>
          <option value="Eshailsat">Eshailsat</option>
          <option value="Azercosmos">Azercosmos</option>
          <option value="Yamalsat">Yamalsat</option>

        </select>
        <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
          <option value="custom">Custom Order</option>
          <option value="asc">Name (A–Z)</option>
          <option value="desc">Name (Z–A)</option>
          <option value="live">Active TV (High → Low)</option>
          <option value="liveAsc">Active TV (Low → High)</option>
        </select>

        {/* 🔒 GLOBAL HIDE / SHOW */}
        <button
          className="hide-values-btn icon"
          onClick={toggleHideValues}
          title={hideValues ? "Show all values" : "Hide all values"}
          type="button"
        >
          {hideValues ? <EyeIcon /> : <EyeOffIcon />}
        </button>

        {/* Manage Groups Button */}
        <button
          className="manage-groups-btn"
          onClick={() => openGroupModal()}
          type="button"
        >
          📁 Manage Groups
        </button>

        {/* View Mode Toggle */}
        <button
          className="view-mode-btn"
          onClick={() => {
            const newMode = viewMode === "group" ? "list" : "group";
            setViewMode(newMode);
            localStorage.setItem("adm_view_mode", newMode);
          }}
          type="button"
          title={viewMode === "group" ? "Switch to List View" : "Switch to Group View"}
        >
          {viewMode === "group" ? "☰ List" : "📁 Groups"}
        </button>
      </div>

      {/* Group Management Modal */}
      {showGroupModal && (
        <div className="modal-overlay" onClick={closeGroupModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingGroup ? "Edit Group" : "Create New Group"}</h3>
              <button className="modal-close" onClick={closeGroupModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Group Name</label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Enter group name..."
                  className="group-name-input"
                />
              </div>

              <div className="form-group">
                <label>Select Channels</label>
                <input
                  type="text"
                  value={channelSearchQuery}
                  onChange={(e) => setChannelSearchQuery(e.target.value)}
                  placeholder="Search channels..."
                  className="channel-search-input"
                />
                <div className="channel-selector">
                  {allChannels
                    .filter((channel) =>
                      channel.name.toLowerCase().includes(channelSearchQuery.toLowerCase())
                    )
                    .map((channel) => (
                      <label key={channel.id} className="channel-checkbox-item">
                        <input
                          type="checkbox"
                          checked={selectedChannelsForGroup.includes(channel.id)}
                          onChange={() => toggleChannelInGroup(channel.id)}
                        />
                        <img loading="lazy" src={channel.logo} alt={channel.name} className="channel-mini-logo" />
                        <span>{channel.name}</span>
                      </label>
                    ))}
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-save" onClick={saveGroup}>
                  Save Group
                </button>
                <button className="btn-cancel" onClick={closeGroupModal}>
                  Cancel
                </button>
              </div>
            </div>

            {/* Existing Groups */}
            {Object.keys(channelGroups).length > 0 && (
              <div className="existing-groups">
                <h4>Existing Groups</h4>
                {Object.entries(channelGroups).map(([groupName, channelIds]) => (
                  <div key={groupName} className="group-item">
                    <div
                      className="group-color-dot"
                      style={{ backgroundColor: getGroupColor(groupName) }}
                    ></div>
                    <span className="group-item-name">{groupName}</span>
                    <span className="group-item-count">({channelIds.length} channels)</span>
                    <button
                      className="btn-edit-group"
                      onClick={() => openGroupModal(groupName)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-delete-group"
                      onClick={() => deleteGroup(groupName)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={
            dragEnabled
              ? displayedChannels
                  .filter((c) => !favSet.has(c.id))
                  .map((c) => c.id)
              : displayedChannels.map((c) => c.id)
          }
          strategy={rectSortingStrategy}
        >
          {/* Group channels by their groups */}
          {viewMode === "group" ? (() => {
            const grouped = {};
            const ungrouped = [];

            displayedChannels.forEach((channel) => {
              const group = getChannelGroup(channel.id);
              if (group) {
                if (!grouped[group.name]) {
                  grouped[group.name] = {
                    color: group.color,
                    channels: [],
                  };
                }
                grouped[group.name].channels.push(channel);
              } else {
                ungrouped.push(channel);
              }
            });

            return (
              <div className="groups-container">
                {/* Render each group as a visual folder */}
                {Object.entries(grouped).map(([groupName, groupData]) => {
                  const isExpanded = expandedGroups[groupName] || false;
                  const hasMore = groupData.channels.length > 4;
                  const displayChannels = isExpanded 
                    ? groupData.channels 
                    : groupData.channels.slice(0, hasMore ? 3 : 4);

                  return (
                    <div key={groupName} className="group-folder">
                      <div 
                        className="group-folder-inner"
                        style={{ borderColor: groupData.color }}
                      >
                        <div className="group-folder-channels">
                          {displayChannels.map((c) => (
                            <SortableCard
                              key={c.id}
                              c={c}
                              favSet={favSet}
                              onToggleFavorite={toggleFavorite}
                              navigate={navigate}
                              dragEnabled={dragEnabled && !favSet.has(c.id)}
                              hideValues={hideValues}
                              revealedChannelId={revealedChannelId}
                              onToggleRevealSingle={toggleRevealSingle}
                              groupName={null}
                              groupColor={null}
                  liveMap={liveMapForCards}
                            />
                          ))}
                          
                          {/* Show More button when collapsed */}
                          {hasMore && !isExpanded && (
                            <div 
                              className="show-all-card"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedGroups(prev => ({
                                  ...prev,
                                  [groupName]: true
                                }));
                              }}
                            >
                              <div className="show-all-content">
                                <span className="show-all-icon">+</span>
                                <span className="show-all-text">
                                  +{groupData.channels.length - 3} more
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Floating Show Less button when expanded */}
                        {hasMore && isExpanded && (
                          <button 
                            className="floating-show-less"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedGroups(prev => ({
                                ...prev,
                                [groupName]: false
                              }));
                            }}
                          >
                            <span className="show-less-icon">−</span>
                            Show Less
                          </button>
                        )}
                        
                        <div 
                          className="group-folder-label"
                          style={{ color: groupData.color }}
                        >
                          {groupName}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Ungrouped channels */}
                {ungrouped.length > 0 && (() => {
                  const isExpanded = expandedGroups.__ungrouped__ || false;
                  const hasMore = ungrouped.length > 4;
                  const displayChannels = isExpanded 
                    ? ungrouped 
                    : ungrouped.slice(0, hasMore ? 3 : 4);

                  return (
                    <div className="group-folder">
                      <div className="group-folder-inner">
                        <div className="group-folder-channels">
                          {displayChannels.map((c) => (
                            <SortableCard
                              key={c.id}
                              c={c}
                              favSet={favSet}
                              onToggleFavorite={toggleFavorite}
                              navigate={navigate}
                              dragEnabled={dragEnabled && !favSet.has(c.id)}
                              hideValues={hideValues}
                              revealedChannelId={revealedChannelId}
                              onToggleRevealSingle={toggleRevealSingle}
                              groupName={null}
                              groupColor={null}
                  liveMap={liveMapForCards}
                            />
                          ))}
                          
                          {/* Show All / Show Less button */}
                          {hasMore && (
                            <div 
                              className="show-all-card"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedGroups(prev => ({
                                  ...prev,
                                  __ungrouped__: !prev.__ungrouped__
                                }));
                              }}
                            >
                              <div className="show-all-content">
                                <span className="show-all-icon">
                                  {isExpanded ? '−' : '+'}
                                </span>
                                <span className="show-all-text">
                                  {isExpanded 
                                    ? 'Show Less' 
                                    : `+${ungrouped.length - 3} more`}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Floating Show Less button */}
                        {hasMore && isExpanded && (
                          <button 
                            className="floating-show-less"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedGroups(prev => ({
                                ...prev,
                                __ungrouped__: false
                              }));
                            }}
                          >
                            <span className="show-less-icon">−</span>
                            Show Less
                          </button>
                        )}
                        
                        <div className="group-folder-label">
                          Ungrouped
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })() : (
            // List View - Original channel grid
            <div className="channel-grid">
              {displayedChannels.map((c) => (
                <SortableCard
                  key={c.id}
                  c={c}
                  favSet={favSet}
                  onToggleFavorite={toggleFavorite}
                  navigate={navigate}
                  dragEnabled={dragEnabled && !favSet.has(c.id)}
                  hideValues={hideValues}
                  revealedChannelId={revealedChannelId}
                  onToggleRevealSingle={toggleRevealSingle}
                  groupName={null}
                  groupColor={null}
                  liveMap={liveMapForCards}
                />
              ))}
            </div>
          )}
        </SortableContext>
        {banners.length > 0 && (
          <BannerWizard
            banners={banners}
            onDone={() => setBanners([])}
          />
        )}
      </DndContext>
    </div>
  );
}
